import Chart from "chart.js/auto";
import { api } from "../api-client.js";
import { renderEnvTabs, type Env } from "../components/env-tabs.js";
import { renderEmptyStateBanner, type ScreenState } from "../components/empty-state-banner.js";
import { bindDatePickerClick } from "../utils/date-input.js";

type Range = "daily" | "weekly" | "monthly" | "yearly" | "custom";
type ChartKind = "line" | "bar";

interface PerformancePoint {
  date: string;
  daily_pnl: number;
  daily_return_pct: number | null;
  cumulative_return_pct: number | null;
  is_live: boolean;
  total_asset: number | null;
  stock_holding_amount: number | null;
}

interface PerformanceResult {
  env: Env;
  state: ScreenState;
  series: PerformancePoint[];
}

const RANGE_LABELS: Record<Range, string> = {
  daily: "일간",
  weekly: "주간",
  monthly: "월간",
  yearly: "연간",
  custom: "직접입력",
};

// 사용자 피드백: "일간과 주간 모두 일별수익이라고 나와" — 범위마다 실제 집계 단위가 다르므로
// 데이터셋 라벨/문구도 그에 맞게 바꾼다(주간=주별 합산, 월간=월별 합산, 연간=연도별 합산).
const PNL_LABEL: Record<Range, string> = {
  daily: "일별 손익",
  weekly: "주별 손익",
  monthly: "월별 손익",
  yearly: "연도별 손익",
  custom: "기간별 손익",
};

const CURRENT_PERIOD_LABEL: Record<Range, string> = {
  daily: "오늘",
  weekly: "이번 주",
  monthly: "이번 달",
  yearly: "올해",
  custom: "오늘",
};

// 브라우저 로컬 날짜를 "YYYY-MM-DD"로 만든다 — toISOString()은 항상 UTC라서 자정 근처에
// 하루가 밀릴 수 있다(이 프로젝트에서 이미 여러 번 겪은 버그 패턴이라 여기서도 피한다).
function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const CUSTOM_RANGE_DEFAULT_DAYS = 90;

// 002 §7 화면3(수익률 차트): 기간 프리셋 + 커스텀 범위 + 라인/막대 토글 + env 전환 탭.
export async function renderPerformance(container: HTMLElement): Promise<void> {
  let env: Env = "운영";
  let range: Range = "daily";
  let kind: ChartKind = "line";
  // 사용자 요청: 직접입력을 처음 열었을 때 빈 값이 아니라 최근 90일이 기본으로 보이게.
  let customTo = localIsoDate(new Date());
  let customFrom = localIsoDate(new Date(Date.now() - (CUSTOM_RANGE_DEFAULT_DAYS - 1) * 24 * 60 * 60 * 1000));
  let chartInstance: Chart | null = null;

  // 프리셋/범위를 바꿀 때마다 화면 전체를 지웠다가 다시 그리면 응답(+차트 렌더링)을 기다리는 동안
  // 깜빡였다 — 제목/탭/필터 UI는 슬롯에 담아 한 번만 배치하고, 차트가 들어갈 결과 영역은 새 내용이
  // 다 준비된 뒤 한 번에 교체한다.
  container.innerHTML = `<h2 class="page-title">수익률</h2>`;
  const tabsSlot = document.createElement("div");
  const controlsSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(tabsSlot, controlsSlot, resultsSlot);

  function renderControls(): void {
    tabsSlot.replaceChildren(
      renderEnvTabs(env, (newEnv) => {
        env = newEnv;
        void load();
      }),
    );

    const controlsFrag = document.createDocumentFragment();

    const presetsRow = document.createElement("div");
    presetsRow.className = "range-presets";
    (Object.keys(RANGE_LABELS) as Range[]).forEach((r) => {
      const btn = document.createElement("button");
      btn.textContent = RANGE_LABELS[r];
      btn.className = r === range ? "active" : "";
      btn.addEventListener("click", () => {
        range = r;
        void load();
      });
      presetsRow.appendChild(btn);
    });
    controlsFrag.appendChild(presetsRow);

    if (range === "custom") {
      const customRow = document.createElement("div");
      customRow.className = "custom-range-row";
      customRow.innerHTML = `
        <input type="date" id="perf-from" value="${customFrom}" />
        <span class="range-sep">~</span>
        <input type="date" id="perf-to" value="${customTo}" />
        <button class="btn-secondary" id="perf-apply">적용</button>
      `;
      bindDatePickerClick(customRow.querySelector<HTMLInputElement>("#perf-from")!);
      bindDatePickerClick(customRow.querySelector<HTMLInputElement>("#perf-to")!);
      customRow.querySelector<HTMLButtonElement>("#perf-apply")!.addEventListener("click", () => {
        customFrom = customRow.querySelector<HTMLInputElement>("#perf-from")!.value;
        customTo = customRow.querySelector<HTMLInputElement>("#perf-to")!.value;
        void load();
      });
      controlsFrag.appendChild(customRow);
    }

    const toggleRow = document.createElement("div");
    toggleRow.className = "chart-toggle";
    (["line", "bar"] as ChartKind[]).forEach((k) => {
      const btn = document.createElement("button");
      btn.textContent = k === "line" ? "라인" : "막대";
      btn.className = k === kind ? "active" : "";
      btn.addEventListener("click", () => {
        kind = k;
        void load();
      });
      toggleRow.appendChild(btn);
    });
    controlsFrag.appendChild(toggleRow);

    controlsSlot.replaceChildren(controlsFrag);
  }

  async function load(): Promise<void> {
    renderControls();

    const params = new URLSearchParams({ env, range });
    if (range === "custom") {
      if (customFrom) params.set("from", customFrom);
      if (customTo) params.set("to", customTo);
    }

    let result: PerformanceResult;
    try {
      result = await api.get<PerformanceResult>(`/api/performance?${params.toString()}`);
    } catch {
      const err = document.createElement("p");
      err.textContent = "데이터를 불러오지 못했습니다.";
      resultsSlot.replaceChildren(err);
      return;
    }

    const frag = document.createDocumentFragment();

    const banner = renderEmptyStateBanner(result.state, env, (newEnv) => {
      env = newEnv;
      void load();
    });
    if (banner) frag.appendChild(banner);

    const card = document.createElement("div");
    card.className = "card";
    const canvas = document.createElement("canvas");
    canvas.style.maxHeight = "360px";
    card.appendChild(canvas);
    frag.appendChild(card);

    let note: HTMLElement | null = null;
    // 오늘자 DAILY_SUMMARY가 아직 없어 체결 이력에서 직접 집계한 잠정 포인트는 색을 다르게
    // 표시한다(Core 공식 집계와 혼동하지 않도록) — 값은 legend가 아니라 라벨/색으로만 구분.
    const hasLivePoint = result.series.length > 0 && result.series.some((p) => p.is_live);
    if (hasLivePoint) {
      note = document.createElement("p");
      note.style.color = "var(--text-dim)";
      note.style.marginTop = "8px";
      note.innerHTML = `<span style="color:#e0824a">●</span> ${CURRENT_PERIOD_LABEL[range]}(잠정) — 체결 내역 기준 실시간 집계, Core 공식 집계 도착 전`;
      frag.appendChild(note);
    }

    // 차트가 들어갈 카드/캔버스까지 포함해 결과 영역을 한 번에 교체한 뒤, 그 새 캔버스에 차트를
    // 그린다 — 이전 차트 인스턴스는 이 시점에 안전하게 destroy한다(예전 캔버스는 이미 화면에서 빠짐).
    resultsSlot.replaceChildren(frag);
    chartInstance?.destroy();
    chartInstance = null;

    if (result.series.length === 0) return;

    const pointColors = result.series.map((p) => (p.is_live ? "#e0824a" : "#d4af6a"));

    // 전체금액/주식보유금액은 항상 area(line+fill)로 겹쳐 그린다 — 라인/막대 토글은 일별손익
    // 데이터셋에만 적용한다(영역을 막대로 바꾸면 참고 화면과 안 맞고 area-fill의 의미도 사라짐).
    // 두 값은 왼쪽 ₩축(y), 손익은 오른쪽 ₩축(y1)을 쓴다 — 손익(수만원)과 총자산(수백~수천만원)의
    // 스케일 차이가 커서 같은 축을 쓰면 손익선이 거의 0으로 눌려 보인다.
    chartInstance = new Chart(canvas, {
      type: "line",
      data: {
        labels: result.series.map((p) => (p.is_live ? `${p.date} (잠정)` : p.date)),
        datasets: [
          {
            type: "line",
            label: "전체금액",
            data: result.series.map((p) => p.total_asset),
            borderColor: "#7c6fd6",
            backgroundColor: "rgba(124,111,214,0.18)",
            fill: true,
            pointRadius: 0,
            borderWidth: 1.5,
            yAxisID: "y",
          },
          {
            type: "line",
            label: "주식보유금액",
            data: result.series.map((p) => p.stock_holding_amount),
            borderColor: "#3ea88f",
            backgroundColor: "rgba(62,168,143,0.28)",
            fill: true,
            pointRadius: 0,
            borderWidth: 1.5,
            yAxisID: "y",
          },
          {
            type: kind,
            label: PNL_LABEL[range],
            data: result.series.map((p) => p.daily_pnl),
            borderColor: "#d4af6a",
            backgroundColor: kind === "bar" ? pointColors : "rgba(212,175,106,0.25)",
            pointBackgroundColor: pointColors,
            pointRadius: result.series.map((p) => (p.is_live ? 6 : 3)),
            yAxisID: "y1",
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          x: { ticks: { color: "#9498ab" }, grid: { color: "#2e3346" } },
          y: { ticks: { color: "#9498ab" }, grid: { color: "#2e3346" } },
          y1: {
            position: "right",
            ticks: { color: "#9498ab" },
            grid: { drawOnChartArea: false },
          },
        },
        plugins: {
          legend: { labels: { color: "#e8e8ee" } },
        },
      },
    });
  }

  await load();
}
